import React, { useRef, useLayoutEffect, useState, useCallback } from "react";
import i18n from "i18next";

/* eslint-disable react/prop-types */

// Collect all navigate actions from a scene → [{from, to}]
function collectEdges(scenes) {
  const edges = [];
  for (const scene of scenes) {
    const collectFromActions = (actions) => {
      for (const a of actions || []) {
        if (a.type === "navigate" && a.targetSceneId) {
          edges.push({ from: scene.id, to: a.targetSceneId });
        }
      }
    };
    for (const el of scene.elements || []) {
      switch (el.type) {
        case "visualtext":
          for (const btn of el.buttons || []) collectFromActions(btn.actions);
          break;
        case "flipcard":
          collectFromActions(el.backActions);
          break;
        case "truefalse":
          collectFromActions(el.correctActions);
          collectFromActions(el.incorrectActions);
          break;
        case "mcq":
          for (const c of el.choices || []) collectFromActions(c.actions);
          break;
        default:
          break;
      }
    }
  }
  // Deduplicate same from→to
  const seen = new Set();
  return edges.filter((e) => {
    const key = `${e.from}→${e.to}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function SceneNode({ scene, isStart, isSelected, onClick }) {
  const elementCount = (scene.elements || []).length;
  return (
    <div
      className={`sce-graph-node${isSelected ? " sce-graph-node--selected" : ""}${isStart ? " sce-graph-node--start" : ""}`}
      onClick={onClick}
      data-node-id={scene.id}
      style={{ background: scene.bgColor || "#1a1a2e", color: scene.textColor || "#fff" }}
    >
      {isStart && (
        <span className="sce-graph-node__start-badge">
          {`▶ ${i18n.t("InteractiveScenario.Label.StartTitleCase") || "Başlangıç"}`}
        </span>
      )}
      <span className="sce-graph-node__title">{scene.title || i18n.t("scene") || "Sahne"}</span>
      <span className="sce-graph-node__meta">
        {elementCount} {i18n.t("InteractiveScenario.Label.ElementCountSuffix") || "element"}
      </span>
    </div>
  );
}

export default function GraphCanvas({
  scenario,
  selectedSceneId,
  onSelectScene,
  onAddScene,
  onRemoveScene,
}) {
  const containerRef = useRef(null);
  const [nodeRects, setNodeRects] = useState({});

  const scenes = scenario.scenes || [];
  const edges = collectEdges(scenes);

  // Measure node positions after render for SVG arrows.
  // Only commit state when the serialized rect values actually changed to avoid infinite loop.
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const rects = {};
    containerRef.current.querySelectorAll("[data-node-id]").forEach((el) => {
      const id = el.dataset.nodeId;
      const parentRect = containerRef.current.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      rects[id] = {
        cx: Math.round(r.left - parentRect.left + r.width / 2),
        cy: Math.round(r.top - parentRect.top + r.height / 2),
        w: Math.round(r.width),
        h: Math.round(r.height),
      };
    });
    setNodeRects((prev) => {
      const next = JSON.stringify(rects);
      return JSON.stringify(prev) === next ? prev : rects;
    });
  }, [scenes.length, selectedSceneId]); // eslint-disable-line react-hooks/exhaustive-deps

  const containerW = containerRef.current?.scrollWidth || 800;
  const containerH = containerRef.current?.scrollHeight || 600;

  return (
    <div className="sce-graph-canvas" ref={containerRef}>
      {/* SVG arrows layer */}
      <svg
        className="sce-graph-canvas__svg"
        width={containerW}
        height={containerH}
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      >
        <defs>
          <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <path d="M0,0 L0,6 L8,3 z" fill="rgba(139,92,246,0.7)" />
          </marker>
        </defs>
        {edges.map((edge) => {
          const from = nodeRects[edge.from];
          const to = nodeRects[edge.to];
          if (!from || !to) return null;
          // Exit right of from-node, enter left of to-node
          const x1 = from.cx + from.w / 2;
          const y1 = from.cy;
          const x2 = to.cx - to.w / 2;
          const y2 = to.cy;
          const mx = (x1 + x2) / 2;
          return (
            <path
              key={`${edge.from}-${edge.to}`}
              d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`}
              stroke="rgba(139,92,246,0.6)"
              strokeWidth={2}
              fill="none"
              markerEnd="url(#arrow)"
            />
          );
        })}
      </svg>

      {/* Scene nodes */}
      <div className="sce-graph-canvas__nodes">
        {scenes.map((scene) => (
          <SceneNode
            key={scene.id}
            scene={scene}
            isStart={scenario.startSceneId === scene.id}
            isSelected={selectedSceneId === scene.id}
            onClick={() => onSelectScene(scene.id)}
          />
        ))}

        <button
          type="button"
          className="sce-graph-canvas__add-scene"
          onClick={onAddScene}
          title={i18n.t("InteractiveScenario.Action.AddScene") || "Yeni Sahne Ekle"}
        >
          <span className="material-icons">add</span>
          {i18n.t("InteractiveScenario.Action.AddScene") || "Sahne Ekle"}
        </button>
      </div>

      {/* Remove selected scene */}
      {selectedSceneId && selectedSceneId !== scenario.startSceneId && (
        <button
          type="button"
          className="sce-graph-canvas__remove-scene"
          onClick={() => onRemoveScene(selectedSceneId)}
          title={i18n.t("InteractiveScenario.Action.DeleteScene") || "Seçili sahneyi sil"}
        >
          <span className="material-icons">delete</span>
          {i18n.t("InteractiveScenario.Action.DeleteScene") || "Sahneyi Sil"}
        </button>
      )}
    </div>
  );
}
